import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { EnvelopeSimple, Phone, MapPin, WhatsappLogo } from '@phosphor-icons/react';
import SEO from '../components/SEO';
import { BreadcrumbSchema } from '../components/StructuredData';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/contact`, form);
      setSubmitted(true);
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <SEO
        title="Contact Us"
        description="Get in touch with Ventriqo. Offices in Bangalore, India and Dubai, UAE. Email: support@ventriqo.com. WhatsApp: +91 9901390180. Support hours: 8AM-8PM India/UAE."
        path="/contact"
      />
      <BreadcrumbSchema items={[{ name: 'Home', path: '/' }, { name: 'Contact', path: '/contact' }]} />

      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-6">Contact</p>
          <h1 className="text-5xl md:text-6xl font-black tracking-tighter leading-none text-black mb-6">
            Get in touch.
          </h1>
          <p className="text-base text-gray-600 max-w-2xl">
            Have a question or ready to get started? Reach out and we'll respond within 24 hours.
          </p>
        </div>
      </section>

      <section data-testid="contact-section" className="bg-[#F9FAFB] border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-black mb-8">Reach us directly</h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <EnvelopeSimple size={24} weight="duotone" className="text-[#002FA7] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-black">Email</p>
                    <p className="text-sm text-gray-600">support@ventriqo.com</p>
                    <p className="text-sm text-gray-600">sales@ventriqo.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <WhatsappLogo size={24} weight="duotone" className="text-[#002FA7] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-black">WhatsApp</p>
                    <p className="text-sm text-gray-600">+91 9901390180</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Phone size={24} weight="duotone" className="text-[#002FA7] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-black">Support Hours</p>
                    <p className="text-sm text-gray-600">8 AM - 8 PM for India/UAE</p>
                    <p className="text-sm text-gray-600">6 PM - 3 AM IST for USA</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <MapPin size={24} weight="duotone" className="text-[#002FA7] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-black">Offices</p>
                    <p className="text-sm text-gray-600 mb-2">Kudlu Gate, Electronic City, Bangalore, India</p>
                    <p className="text-sm text-gray-600">Salman Commercial Tower, Business Bay, Dubai, UAE</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              {submitted ? (
                <div data-testid="contact-form-success" className="text-center py-16 border border-[#002FA7]/20 bg-white">
                  <p className="text-xl font-bold text-black mb-2">Message sent!</p>
                  <p className="text-sm text-gray-600">We'll get back to you within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4 bg-white border border-black/10 p-8">
                  <div>
                    <Label htmlFor="contact-name" className="text-sm font-medium text-black">Name</Label>
                    <Input data-testid="contact-form-name" id="contact-name" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1 rounded-[4px] border-black/20 focus:ring-2 focus:ring-[#002FA7]" />
                  </div>
                  <div>
                    <Label htmlFor="contact-email" className="text-sm font-medium text-black">Email</Label>
                    <Input data-testid="contact-form-email" id="contact-email" type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-1 rounded-[4px] border-black/20 focus:ring-2 focus:ring-[#002FA7]" />
                  </div>
                  <div>
                    <Label htmlFor="contact-phone" className="text-sm font-medium text-black">Phone</Label>
                    <Input data-testid="contact-form-phone" id="contact-phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="mt-1 rounded-[4px] border-black/20 focus:ring-2 focus:ring-[#002FA7]" />
                  </div>
                  <div>
                    <Label htmlFor="contact-message" className="text-sm font-medium text-black">Message</Label>
                    <Textarea data-testid="contact-form-message" id="contact-message" rows={5} required value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="mt-1 rounded-[4px] border-black/20 focus:ring-2 focus:ring-[#002FA7]" />
                  </div>
                  <Button data-testid="contact-form-submit" type="submit" disabled={submitting} className="w-full bg-[#002FA7] hover:bg-[#002277] text-white rounded-[4px] py-3 text-sm font-semibold">
                    {submitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
